// src/hooks/usePortfolioData.js
import { useState, useEffect } from 'react';
import { collection, getDocs, onSnapshot, doc, query, where, documentId, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase/config';

import { usePortfolio } from '../contexts/PortfolioContext';

export const usePortfolioData = (userEmail) => {
  const [portfolioData, setPortfolioData] = useState({
    totalValue: 0,
    totalInvested: 0,
    portfolioItems: [],
    totalProfit: 0,
    dailyChange: 0,
    dailyChangePercent: 0,
    targetPortfolio: {},
    settings: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [symbolMap, setSymbolMap] = useState(new Map());
  const { selectedPortfolio } = usePortfolio();

  useEffect(() => {
    // Portföy değiştiğinde verileri sıfırla
    setPortfolioData({
      totalValue: 0,
      totalInvested: 0,
      portfolioItems: [],
      totalProfit: 0,
      dailyChange: 0,
      dailyChangePercent: 0,
      targetPortfolio: {},
      settings: null
    });
    setSymbolMap(new Map());
    
    if (!userEmail || !selectedPortfolio) {
      setIsLoading(false);
      return;
    }

    const fetchSymbolMap = async (positions) => {
      try {
        const symbols = new Set();
        positions.forEach(doc => symbols.add(doc.id));

        if (symbols.size === 0) return;

        const symbolBatches = Array.from(symbols).reduce((batches, symbol, i) => {
          const batchIndex = Math.floor(i / 10);
          if (!batches[batchIndex]) batches[batchIndex] = [];
          batches[batchIndex].push(symbol);
          return batches;
        }, []);

        const newSymbolMap = new Map();

        for (const batch of symbolBatches) {
          const q = query(
            collection(db, 'tickerMap'),
            where(documentId(), 'in', batch)
          );
          const snapshot = await getDocs(q);
          snapshot.forEach(doc => {
            newSymbolMap.set(doc.id, doc.data());
          });
        }

        setSymbolMap(newSymbolMap);
      } catch (error) {
        console.error('Symbol map loading error:', error);
      }
    };

    console.log('Starting portfolio data subscription for user:', userEmail);
    setIsLoading(true);

    const userDoc = doc(db, 'users', userEmail);
    const portfolioDoc = doc(userDoc, 'portfolios', selectedPortfolio.id);
    const positionsRef = collection(portfolioDoc, 'positions');
    const portfolioRef = collection(portfolioDoc, 'portfolio');

    // Load portfolio-specific settings
    const loadPortfolioSettings = async () => {
      if (selectedPortfolio) {
        const portfolioDoc = doc(userDoc, 'portfolios', selectedPortfolio.id);
        const settingsRef = doc(portfolioDoc, 'settings', 'rebalance');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const settings = settingsSnap.data();
          setPortfolioData(prev => ({
            ...prev,
            settings
          }));
        }
      }
    };

    loadPortfolioSettings();

    console.log(`Loading data for portfolio: ${selectedPortfolio.name} (${selectedPortfolio.id})`);
    const unsubscribe = onSnapshot(positionsRef, async (currentPositionsSnapshot) => {
      try {
        if (symbolMap.size === 0) {
          await fetchSymbolMap(currentPositionsSnapshot);
        }

        const targetPortfolioSnapshot = await getDocs(portfolioRef);
        const targetPortfolio = {};
        targetPortfolioSnapshot.forEach(doc => {
          targetPortfolio[doc.id] = doc.data();
        });

        const positionsByCategory = {};
        let totalPortfolioValue = 0;
        let totalInvested = 0;
        let totalProfit = 0;

        // Hedef portföy yapısına göre kategorileri oluştur
        Object.entries(targetPortfolio).forEach(([category, data]) => {
          if (category === 'TRY.TRY.CURRENCY' || category === 'VIST.USD.NYSE') return;

          positionsByCategory[category] = {
            name: category,
            targetAllocation: data.allocation || 0,
            value: 0,
            invested: 0,
            gain: 0,
            gainPercent: 0,
            currentAllocation: 0,
            items: [],
            subCategories: []
          };

          if (data.subCategories) {
            Object.entries(data.subCategories).forEach(([subName, subData]) => {
              const subCategory = {
                name: subName,
                targetAllocation: subData.allocation || 0,
                value: 0,
                invested: 0,
                gain: 0,
                gainPercent: 0,
                currentAllocation: 0,
                items: []
              };

              if (subData.assets) {
                Object.entries(subData.assets).forEach(([symbol, assetData]) => {
                  const symbolInfo = symbolMap.get(symbol);
                  subCategory.items.push({
                    symbol,
                    name: symbolInfo?.description || symbol,
                    exchange: symbolInfo?.exchange,
                    value: 0,
                    invested: 0,
                    gain: 0,
                    gainPercent: 0,
                    description: symbolInfo?.description,
                    targetAllocation: assetData.allocation || 0,
                    currentAllocation: 0
                  });
                });
              }

              positionsByCategory[category].subCategories.push(subCategory);
            });
          }

          if (data.assets) {
            Object.entries(data.assets).forEach(([symbol, assetData]) => {
              const symbolInfo = symbolMap.get(symbol);
              positionsByCategory[category].items.push({
                symbol,
                name: symbolInfo?.description || symbol,
                exchange: symbolInfo?.exchange,
                value: 0,
                invested: 0,
                gain: 0,
                gainPercent: 0,
                description: symbolInfo?.description,
                targetAllocation: assetData.allocation || 0,
                currentAllocation: 0
              });
            });
          }
        });

        // Mevcut pozisyonları işle
        currentPositionsSnapshot.forEach(doc => {
          const position = doc.data();
          const symbol = doc.id;

          let mainCategory = '';
          let subCategory = '';

          Object.entries(targetPortfolio).forEach(([category, data]) => {
            if (data.assets && symbol in data.assets) {
              mainCategory = category;
            } else if (data.subCategories) {
              Object.entries(data.subCategories).forEach(([sub, subData]) => {
                if (subData.assets && symbol in subData.assets) {
                  mainCategory = category;
                  subCategory = sub;
                }
              });
            }
          });

          if (!mainCategory || mainCategory === 'TRY.TRY.CURRENCY' || mainCategory === 'VIST.USD.NYSE') return;

          // Değer ve kazanç hesaplamaları
          const quantity = position.quantity || 0;
          const avgCost = position.avgCost || 0;
          const lastPrice = position.lastPrice || avgCost;

          const currentValue = lastPrice * quantity;
          const invested = avgCost * quantity;
          const gain = currentValue - invested;
          const gainPercent = invested ? (gain / invested) * 100 : 0;

          if (subCategory) {
            const subCategoryData = positionsByCategory[mainCategory].subCategories
              .find(sub => sub.name === subCategory);

            if (subCategoryData) {
              const existingItem = subCategoryData.items.find(item => item.symbol === symbol);
              if (existingItem) {
                Object.assign(existingItem, {
                  ...position,
                  value: currentValue,
                  invested: invested,
                  gain: gain,
                  gainPercent: gainPercent,
                  lastPrice: lastPrice
                });
              }

              subCategoryData.value += currentValue;
              subCategoryData.invested += invested;
              subCategoryData.gain += gain;
            }
          } else {
            const existingItem = positionsByCategory[mainCategory].items.find(item => item.symbol === symbol);
            if (existingItem) {
              Object.assign(existingItem, {
                ...position,
                value: currentValue,
                invested: invested,
                gain: gain,
                gainPercent: gainPercent,
                lastPrice: lastPrice
              });
            }
          }

          positionsByCategory[mainCategory].value += currentValue;
          positionsByCategory[mainCategory].invested += invested;
          positionsByCategory[mainCategory].gain += gain;

          totalPortfolioValue += currentValue;
          totalInvested += invested;
          totalProfit += gain;
        });

        // Allocation ve yüzde hesaplamaları
        Object.values(positionsByCategory).forEach(category => {
          category.currentAllocation = totalPortfolioValue ? (category.value / totalPortfolioValue) * 100 : 0;
          category.gainPercent = category.invested ? (category.gain / category.invested) * 100 : 0;

          category.items.forEach(item => {
            item.currentAllocation = category.value ? (item.value / category.value) * 100 : 0;
          });

          category.subCategories.forEach(sub => {
            sub.currentAllocation = category.value ? (sub.value / category.value) * 100 : 0;
            sub.gainPercent = sub.invested ? (sub.gain / sub.invested) * 100 : 0;

            sub.items.forEach(item => {
              item.currentAllocation = sub.value ? (item.value / sub.value) * 100 : 0;
            });
          });
        });

        setPortfolioData(prev => ({
          ...prev,
          totalValue: totalPortfolioValue,
          totalInvested,
          portfolioItems: Object.values(positionsByCategory),
          totalProfit,
          dailyChange: totalProfit,
          dailyChangePercent: totalInvested ? (totalProfit / totalInvested) * 100 : 0,
          targetPortfolio
        }));

      } catch (error) {
        console.error('Portfolio data loading error:', error);
        setError(error);
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [userEmail, selectedPortfolio?.id]); // symbolMap'i dependency'den çıkar, selectedPortfolio.id'yi ekle

  return { portfolioData, isLoading, error };
};